import { EnergySystem } from '@/types/energySystem'
import { utilityProgramCollection } from '@/reducers/utilityPrograms'
import { energySystemEnrollmentCollection } from '@/reducers/deviceProgramEnrollments'
import { utilityProgramEnrollmentCollection } from '@/reducers/utilityProgramEnrollments'
import useDeviceEnrollments, {
  useDeviceEligibility,
  useUserHasProgramEnrollment,
} from '@/hooks/useDeviceEnrollments/useDeviceEnrollments'

type Props = {
  energySystem: EnergySystem
}

const useEnergySystemHasEnrolledInEligibleProgram = (
  energySystem: EnergySystem,
  programsEnergySystemIsEligibleFor: number[],
) => {
  const { deviceEnrollments } = useDeviceEnrollments(
    'energySystem',
    energySystem.id,
  )
  return deviceEnrollments.some(({ utilityProgram }) =>
    programsEnergySystemIsEligibleFor.includes(utilityProgram.id),
  )
}

function useProgramsEnergySystemIsEligibleFor(energySystem: EnergySystem) {
  const { deviceEligibilities, isLoading } = useDeviceEligibility(
    'energySystem',
    energySystem.id,
  )

  const programsEnergySystemIsEligibleFor = deviceEligibilities
    .filter(({ eligibility }) => eligibility?.is_eligible)
    .map(({ utilityProgram }) => utilityProgram.id)

  return {
    programsEnergySystemIsEligibleFor,
    isLoading,
  }
}

export function useViewModel(props: Props) {
  const { isLoadingOrIdle: energySystemEnrollmentIsLoadingOrIdle } =
    energySystemEnrollmentCollection.useFetch()
  const { isLoadingOrIdle: utilityProgramEnrollmentIsLoadingOrIdle } =
    utilityProgramEnrollmentCollection.useFetch()
  const { isLoadingOrIdle: utilityProgramIsLoadingOrIdle } =
    utilityProgramCollection.useFetch()
  const { programsEnergySystemIsEligibleFor, isLoading: isLoadingEligibility } =
    useProgramsEnergySystemIsEligibleFor(props.energySystem)

  const {
    userHasProgramEnrollment: userHasProgramEnrollmentEnergySystemIsEligibleFor,
    isLoading: isLoadingUserHasProgramEnrollment,
  } = useUserHasProgramEnrollment(programsEnergySystemIsEligibleFor)

  const isLoading =
    energySystemEnrollmentIsLoadingOrIdle ||
    utilityProgramEnrollmentIsLoadingOrIdle ||
    utilityProgramIsLoadingOrIdle ||
    isLoadingEligibility ||
    isLoadingUserHasProgramEnrollment

  const energySystemHasEnrolledInEligibleProgram =
    useEnergySystemHasEnrolledInEligibleProgram(
      props.energySystem,
      programsEnergySystemIsEligibleFor,
    )

  const userHasEnrolledInProgramButNotEnergySystem =
    userHasProgramEnrollmentEnergySystemIsEligibleFor &&
    !energySystemHasEnrolledInEligibleProgram

  return {
    energySystem: props.energySystem,
    energySystemHasEnrolledInEligibleProgram,
    userHasEnrolledInProgramButNotEnergySystem,
    isLoading,
    programsEnergySystemIsEligibleFor,
  }
}
